import { useTranslation } from 'react-i18next'

const fmt = (v) => (v == null ? '—' : Math.round(v))
const pct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${Math.round(v)} %`)

function VolumeComparison({ title, link, lowLabel, highLabel }) {
  const { t } = useTranslation()
  return (
    <div className="border rounded-lg p-3" data-testid="volume-link">
      <h4 className="text-sm font-semibold">{title}</h4>
      {link.sufficient_data ? (
        <>
          <dl className="grid grid-cols-2 gap-2 text-xs mt-2">
            <div><dt className="text-gray-600">{lowLabel}</dt><dd>{t('trainingAnalysis.links.group', { volume: fmt(link.low.mean_volume_kg), sessions: link.low.sessions })}</dd></div>
            <div><dt className="text-gray-600">{highLabel}</dt><dd>{t('trainingAnalysis.links.group', { volume: fmt(link.high.mean_volume_kg), sessions: link.high.sessions })}</dd></div>
          </dl>
          <p className="text-xs mt-1">{t('trainingAnalysis.links.difference', { pct: pct(link.difference_pct) })}</p>
        </>
      ) : (
        <p className="text-xs text-gray-600 mt-1" data-testid="not-enough-data">
          {t('trainingAnalysis.links.notEnoughData', { min: link.min_sessions, low: link.low.sessions, high: link.high.sessions })}
        </p>
      )}
    </div>
  )
}

/** VH-225: training's direct links — carbs, sleep, recovery, sessions before a blood draw. */
export default function TrainingLinksSection({ links }) {
  const { t } = useTranslation()
  const { carbs, sleep, recovery, pre_draw: preDraw } = links
  return (
    <section className="space-y-3" data-testid="training-links">
      <h3 className="text-base font-semibold">{t('trainingAnalysis.links.title')}</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <VolumeComparison title={t('trainingAnalysis.links.carbsTitle')} link={carbs}
          lowLabel={t('trainingAnalysis.links.carbsLow', { g: carbs.threshold_g })}
          highLabel={t('trainingAnalysis.links.carbsHigh', { g: carbs.threshold_g })} />
        <VolumeComparison title={t('trainingAnalysis.links.sleepTitle')} link={sleep}
          lowLabel={t('trainingAnalysis.links.sleepShort', { h: sleep.short_under_minutes / 60 })}
          highLabel={t('trainingAnalysis.links.sleepLong', { h: sleep.long_over_minutes / 60 })} />
        <div className="border rounded-lg p-3" data-testid="recovery-link">
          <h4 className="text-sm font-semibold">{t('trainingAnalysis.links.recoveryTitle')}</h4>
          {recovery.status === 'insufficient_data' ? (
            <p className="text-xs text-gray-600 mt-1" data-testid="not-enough-data">{t('trainingAnalysis.links.recoveryNotEnough')}</p>
          ) : (
            <>
              <p className="text-xs mt-1">{t(`trainingAnalysis.links.recovery.${recovery.status}`)}</p>
              <p className="text-xs text-gray-600">
                {t('trainingAnalysis.links.recoveryChange', { hr: recovery.resting_hr_change_bpm ?? '—', deep: recovery.deep_minutes_change ?? '—' })}
              </p>
            </>
          )}
        </div>
        <div className="border rounded-lg p-3" data-testid="pre-draw-link">
          <h4 className="text-sm font-semibold">{t('trainingAnalysis.links.preDrawTitle')}</h4>
          {preDraw.length === 0 ? (
            <p className="text-xs text-gray-600 mt-1" data-testid="not-enough-data">{t('trainingAnalysis.links.preDrawNone')}</p>
          ) : (
            <ul className="text-xs space-y-2 mt-1">
              {preDraw.map((d) => (
                <li key={d.blood_test_date}>
                  <p className="font-medium">{t('trainingAnalysis.links.preDrawTest', { date: d.blood_test_date, days: d.window_days })}</p>
                  {d.sessions.length === 0
                    ? <p className="text-gray-600">{t('trainingAnalysis.links.preDrawNoSessions')}</p>
                    : d.sessions.map((s) => (
                      <p key={s.session_id} className="text-gray-600">
                        {t('trainingAnalysis.links.preDrawSession', { date: s.date, sets: s.working_sets, exercises: s.exercises.join(', ') })}
                      </p>
                    ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}
